import React, { useEffect, useState } from "react";
import { enterFullscreen, fullscreenSupported, isFullscreen, subscribeFullscreen } from "../shared/fullscreen";
import styles from "./FullscreenNudge.module.scss";

// On a phone the browser's own bars take a real share of the screen, upright
// and even more so sideways. This is a slim pill along the bottom, not a
// dialog: one tap goes fullscreen (the tap is the gesture the browser needs;
// nothing can grant it on its own), ✕ dismisses it for the visit, and the ⛶
// button in the bar stays for later. On iPhone, where Safari has no page
// fullscreen, the pill offers the Home Screen install instead, which runs
// without the bars; shown at most a few times ever. Never during a match (it
// has its own fullscreen) or in an installed app.

const TOUCH = "(pointer: coarse)";
const SESSION_KEY = "ar.fsNudge.v2";
const IOS_COUNT_KEY = "ar.landscapeHint.count";
const IOS_MAX_SHOWS = 3;

function installed(): boolean {
  try {
    return matchMedia("(display-mode: standalone)").matches || matchMedia("(display-mode: fullscreen)").matches || (navigator as any).standalone === true;
  } catch {
    return false;
  }
}
function onIPhone(): boolean {
  return /iPhone|iPod/i.test(navigator.userAgent);
}
function matchOnScreen(): boolean {
  return !!document.querySelector('.page-main-game, [class*="game-stage"]');
}
function dismissed(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}
function dismiss(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}
function iosShows(): number {
  try {
    return Number(localStorage.getItem(IOS_COUNT_KEY) || "0") || 0;
  } catch {
    return 0;
  }
}
function bumpIosShows(): void {
  try {
    localStorage.setItem(IOS_COUNT_KEY, String(iosShows() + 1));
  } catch {
    /* ignore */
  }
}

type Kind = "fullscreen" | "ios";

// A screen with its own full screen button hides the pill while it is up.
let ownButtons = 0;
const ownListeners = new Set<() => void>();
export function useOwnFullscreenButton(on: boolean): void {
  useEffect(() => {
    if (!on) return;
    ownButtons++;
    ownListeners.forEach((f) => f());
    return () => {
      ownButtons--;
      ownListeners.forEach((f) => f());
    };
  }, [on]);
}

export default function FullscreenNudge() {
  const [kind, setKind] = useState<Kind | null>(null);
  const [steps, setSteps] = useState(false);

  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(TOUCH);
    let iosCounted = false;
    const evaluate = () => {
      if (!mq.matches || dismissed() || installed() || isFullscreen() || matchOnScreen() || ownButtons > 0) {
        setKind(null);
        return;
      }
      if (fullscreenSupported()) setKind("fullscreen");
      else if (onIPhone() && iosShows() < IOS_MAX_SHOWS) {
        if (!iosCounted) {
          iosCounted = true;
          bumpIosShows();
        }
        setKind("ios");
      } else setKind(null);
    };
    evaluate();
    mq.addEventListener("change", evaluate);
    const unsub = subscribeFullscreen(evaluate);
    ownListeners.add(evaluate);
    return () => {
      mq.removeEventListener("change", evaluate);
      ownListeners.delete(evaluate);
      unsub();
    };
  }, []);

  if (!kind) return null;

  const close = () => {
    dismiss();
    setKind(null);
  };

  return (
    <div className={styles.pill} role="status">
      {kind === "fullscreen" ? (
        <button
          type="button"
          className={styles.go}
          onClick={() => {
            enterFullscreen(); // inside the tap, as the browser requires
            dismiss();
            setKind(null);
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
          </svg>
          Go full screen
        </button>
      ) : (
        <>
          <button type="button" className={styles.go} onClick={() => setSteps((v) => !v)} aria-expanded={steps}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12M8 7l4-4 4 4M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
            </svg>
            Full screen on iPhone
          </button>
          {steps && (
            <span className={styles.steps}>
              Share, then <b>Add to Home Screen</b>, then Add. It opens without Safari's bars.
            </span>
          )}
        </>
      )}
      <button type="button" className={styles.close} onClick={close} aria-label="Not now">
        ✕
      </button>
    </div>
  );
}
